import React, { useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Map as ImmutableMap } from 'immutable'
import { selectAuth } from '../../services/auth/selectors'
import { createRelative } from '../../services/url/index'
import { oracleMarkdownToHtml } from '../../services/markdown'
import OracleVoiceModeIcon from '../../icons/OracleVoiceModeIcon'
import { getOracleColor } from '../OracleCommons'
import { MediaTileCollection, MediaTileLayoutDirection } from './MediaTileCollection'
import { RelatedDocument } from './MediaTile'
import { trackAiRecommendationClick } from './utils/aiRecommendationTracking'
import {
  extractGaiaTags,
  renderGaiaPlaceholders,
  type DocumentMap,
  type DocumentEntry,
} from '../OracleCommons/gaiaTagParser'
import { type EnrichedDocument } from './hooks/useOracleChatSubscription'
import { OracleCardRenderer } from './OracleCardRenderer'
import { DivinationCardRenderer } from './DivinationCardRenderer'

interface OracleAgentMessageProps {
  text: string
  mode?: 'text' | 'voice'
  documents?: EnrichedDocument[]
  tileLayout?: MediaTileLayoutDirection
  maxTiles?: number
  isStreaming?: boolean
  createdAt?: string
}

/**
 * Normalizes a URL path by removing query params and hash
 * Falls back to manual parsing if createRelative fails
 */
function normalizePathFromHref (href: string): string | null {
  try {
    return createRelative(href)
  } catch {
    // Fallback: extract path from href attribute directly
    const [pathWithoutQuery] = href.split('?')
    const [pathWithoutHash] = pathWithoutQuery.split('#')
    return pathWithoutHash
  }
}

export const OracleAgentMessage: React.FC<OracleAgentMessageProps> = ({
  text,
  mode = 'text',
  documents = [],
  tileLayout = 'row',
  maxTiles,
  isStreaming = false,
  createdAt,
}) => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth) || ImmutableMap()
  const textRef = useRef<HTMLDivElement>(null)
  const isVoiceMode = mode === 'voice'

  // Extract Gaia tags from text and separate documents into content links vs tiles
  const { processedText, tagDict } = useMemo(() => extractGaiaTags(text), [text])

  // Build document map and tile documents from the documents array
  const {
    contentLinkMap, tileDocuments, oracleCardDocuments, tarotDocuments,
  } = useMemo(() => {
    const docMap: DocumentMap = new Map<string, DocumentEntry>()
    const tiles: EnrichedDocument[] = []
    const oracleCards: EnrichedDocument[] = []
    const tarotCards: EnrichedDocument[] = []

    // Collect componentIds that are referenced by Gaia tags in the text
    const referencedIds = new Set(Object.values(tagDict).map((tag) => tag.id))

    documents.forEach((doc) => {
      // Oracle card documents are collected separately
      if (doc.oracle) {
        oracleCards.push(doc)
        return
      }

      // Tarot card documents are collected separately
      if (doc.tarot) {
        tarotCards.push(doc)
        return
      }

      // Build document map for inline link rendering
      if (doc.componentId && referencedIds.has(doc.componentId)) {
        const entry: DocumentEntry = { componentType: doc.componentType || '' }

        if (doc.componentType === 'contentLink') {
          entry.contentLink = {
            contentType: doc.contentType,
            contentId: doc.contentId,
            title: doc.title,
            path: doc.contentLinkPath || doc.path,
          }
        }

        docMap.set(doc.componentId, entry)
      }

      // Documents with thumbnails also render as media tiles
      if (doc.thumb) {
        tiles.push(doc)
      }
    })

    return {
      contentLinkMap: docMap,
      tileDocuments: tiles,
      oracleCardDocuments: oracleCards,
      tarotDocuments: tarotCards,
    }
  }, [documents, tagDict])

  // Render HTML: extract tags → markdown → resolve placeholders
  const htmlString = useMemo(() => {
    const htmlContent = oracleMarkdownToHtml({ markdown: processedText })
    const rawHtml = Array.isArray(htmlContent) ? htmlContent.join('') : htmlContent || ''

    // Only run placeholder replacement if there are Gaia tags
    if (Object.keys(tagDict).length === 0) {
      return rawHtml
    }

    return renderGaiaPlaceholders(rawHtml, tagDict, contentLinkMap)
  }, [processedText, tagDict, contentLinkMap])

  const iconColor = getOracleColor('--oracle-text-secondary', 'oracle-n60')

  // Create a map of normalized paths to documents for quick lookup when links are clicked
  const pathToDocumentMap = useMemo(() => {
    const map = new Map<string, RelatedDocument>()
    documents.forEach((doc) => {
      if (doc.path) {
        const normalizedPath = createRelative(doc.path)
        map.set(normalizedPath, doc)
      }
    })
    return map
  }, [documents])

  // Track clicks on links in markdown that correspond to AI recommendations
  useEffect(() => {
    const textElement = textRef.current
    if (!textElement || documents.length === 0) {
      return
    }

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest('a')
      if (!link?.href) {
        return
      }

      // Normalize the href and find matching document
      const normalizedPath = normalizePathFromHref(link.href)
      if (!normalizedPath) {
        return
      }

      const document = pathToDocumentMap.get(normalizedPath)
      if (document) {
        trackAiRecommendationClick(document, dispatch, auth)
      }
    }

    textElement.addEventListener('click', handleLinkClick)
    // eslint-disable-next-line consistent-return
    return () => {
      textElement.removeEventListener('click', handleLinkClick)
    }
  }, [documents, pathToDocumentMap, dispatch, auth])

  return (
    <div className={`oracle-chatbot-message ${isVoiceMode ? 'oracle-chatbot-message--voice' : ''}`}>
      <div className="oracle-chatbot-message__content">
        {isVoiceMode && (
          <div className="oracle-chatbot-message__voice-icon">
            <OracleVoiceModeIcon color={iconColor} />
          </div>
        )}
        <div className="oracle-chatbot-message__text">
          {tarotDocuments.length === 0 && (
            <div ref={textRef} dangerouslySetInnerHTML={{ __html: htmlString }} />
          )}
          {!isStreaming && tileDocuments && tileDocuments.length > 0 && (
            <div className="oracle-chatbot-message__video-tiles">
              <MediaTileCollection
                documents={tileDocuments}
                layout={tileLayout}
                maxTiles={maxTiles}
              />
            </div>
          )}
          {!isStreaming && oracleCardDocuments.length > 0 && (
            <div className="oracle-chatbot-message__oracle-cards">
              {oracleCardDocuments.map((doc) => (
                doc.oracle && (
                  <OracleCardRenderer
                    key={doc.oracle.pullId}
                    oracle={doc.oracle}
                    createdAt={createdAt}
                  />
                )
              ))}
            </div>
          )}
          {tarotDocuments.length > 0 && (
            <div className="oracle-chatbot-message__tarot-cards">
              {tarotDocuments.map((doc) => (
                doc.tarot && (
                  <DivinationCardRenderer
                    key={doc.tarot.pullId}
                    objects={doc.tarot.objects}
                    pulledAt={doc.tarot.pulledAt || undefined}
                  >
                    <div ref={textRef} dangerouslySetInnerHTML={{ __html: htmlString }} />
                  </DivinationCardRenderer>
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
